/**
 * Marketing attribution helper (T2-6448).
 *
 * The shared shell capture owns each coherent visit and its expiry. Signup
 * sends its three acquisition snapshots; legacy Location columns receive a
 * projection of the first visit. Older shells without the shared helper can
 * still use the `marketing_attribution` storage fallback.
 */

export const MARKETING_ATTRIBUTION_STORAGE_KEY = 'marketing_attribution';

/** Kept separate from legacy Location columns: each touch is one coherent visit. */
export const getAcquisitionSnapshotPayload = (): Record<string, unknown> => {
  try {
    const helper = (window as any).TempStarsAttribution;
    const saved = helper ? helper.read(window.localStorage, Date.now()) : {};
    const { firstExternal, signupLastNonDirect, latestPaid } = saved;
    return { firstExternal, signupLastNonDirect, latestPaid };
  } catch (_) { return {}; }
};

export type MarketingAttribution = {
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  gclid?: string | null;
  wbraid?: string | null;
  gbraid?: string | null;
  landingPage?: string | null;
  referrer?: string | null;
  attributedAt?: string | null;
};

const ATTRIBUTION_KEYS: (keyof MarketingAttribution)[] = [
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmContent',
  'utmTerm',
  'gclid',
  'wbraid',
  'gbraid',
  'landingPage',
  'referrer',
  'attributedAt',
];

/**
 * Returns the stored first-touch attribution fields, or an empty object when none
 * exists, the record is expired, or storage is unavailable/corrupt. Only non-empty
 * values are included so a clean signup (no tracking) sends nothing.
 */
export const getMarketingAttribution = (): MarketingAttribution => {
  // When the shared capture is available it is authoritative, including an
  // empty result after rejecting conflicting/expired handoff evidence.
  try {
    const helper = (window as any).TempStarsAttribution;
    if (helper) {
      const first = helper.read(window.localStorage, Date.now()).firstExternal;
      const result: MarketingAttribution = {};
      ATTRIBUTION_KEYS.forEach(key => {
        if (typeof first?.[key] === 'string') result[key] = first[key];
      });
      return result;
    }
  } catch (_) { return {}; }
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(MARKETING_ATTRIBUTION_STORAGE_KEY);
  } catch (e) {
    return {};
  }
  if (!raw) return {};

  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return {};
  }
  if (!parsed || typeof parsed !== 'object') return {};

  // Respect the ~90-day expiry written alongside the record.
  if (parsed.expires_at) {
    const expiresAt = new Date(parsed.expires_at).getTime();
    if (!Number.isNaN(expiresAt) && expiresAt <= Date.now()) {
      return {};
    }
  }

  const result: MarketingAttribution = {};
  ATTRIBUTION_KEYS.forEach((key) => {
    const value = parsed[key];
    if (value === undefined || value === null) return;
    const str = String(value).trim();
    if (str) {
      result[key] = str;
    }
  });

  return result;
};
